import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { ThemeProvider } from '@material-ui/core/styles'
import themes, { extendTheme } from 'themes'
import { gtmEvent } from 'lib/gtm'
import { useAuth } from 'lib/auth'
import SchedulePageBackground from 'pages/schedule/components/page-background'
import PrivateLessonConfirmation from './confirmation'
import {
  PurchaseFailed,
  PurchasePending,
  PurchaseFallback,
} from './status-cards'
import { PAGE_BACKGROUND } from './styles'
import { enrollmentToConfirmationProps } from './utils'

const refreshTheme = extendTheme(themes.offeringsRefresh)

const reloadPage = () => window.location.reload()

// What the paid private lesson return page shows with
// FLAG_FEAT_PRIVATE_BOOKING_REDESIGN on, replacing the shared "Thank you!"
// header, error disclaimer and `PrivateLessonPurchaseConfirmation` card:
//   - canceled                      -> purchase not completed (retry)
//   - `failed` (could not verify)   -> couldn't verify, reload
//   - complete + booking loaded     -> the confirmation
//   - complete + lookup failed      -> payment received, reload
//   - anything else                 -> confirming your payment
// Buttons keep the legacy GTM events and destinations.
const PrivateLessonPurchaseReturn = ({
  purchaseStatus,
  enrollment,
  error,
  retryTo,
}) => {
  const { isCoach, user } = useAuth()
  const { idOrSlug, lessonTypeId } = useParams()
  const { lesson, transactions } = enrollment || {}
  const { coach, coachFacility } = lesson || {}

  const coachBooksOnBehalfOfStudent =
    user?.coach?.coachProfile?.slug === idOrSlug ||
    user?.externalId === idOrSlug
  const bookNewSessionUrl = `/coach/${idOrSlug}/schedule?lessonTypeId=${lessonTypeId}&locationId=${coachFacility?.id}`
  const coachBookingUrl = coach?.bookingUrl || ''

  const onBookAnotherSession = useCallback(() => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-another-session-button',
    })
    window.open(bookNewSessionUrl, '_blank')
  }, [bookNewSessionUrl])

  const onViewOfferings = useCallback(() => {
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-view-coach-offerings-button',
    })
    if (coachBookingUrl) {
      window.location.href = coachBookingUrl
    }
  }, [coachBookingUrl])

  let content
  if (purchaseStatus === 'canceled') {
    content = <PurchaseFailed retryTo={retryTo} />
  } else if (purchaseStatus === 'failed') {
    content = <PurchaseFallback onReload={reloadPage} />
  } else if (purchaseStatus === 'complete' && enrollment) {
    const props = enrollmentToConfirmationProps(enrollment)
    // Same rule as the legacy card: coaches and free lessons show no total.
    const paymentRequired = !isCoach && transactions?.[0]?.amountInCents > 0
    content = (
      <PrivateLessonConfirmation
        {...props}
        payment={paymentRequired ? props.payment : null}
        coachIsViewingOwnSchedule={coachBooksOnBehalfOfStudent}
        onViewOfferings={onViewOfferings}
        onBookAnotherSession={onBookAnotherSession}
      />
    )
  } else if (purchaseStatus === 'complete' && error) {
    content = <PurchaseFallback verified onReload={reloadPage} />
  } else {
    content = <PurchasePending />
  }

  return (
    <ThemeProvider theme={refreshTheme}>
      <SchedulePageBackground color={PAGE_BACKGROUND}>
        {content}
      </SchedulePageBackground>
    </ThemeProvider>
  )
}

PrivateLessonPurchaseReturn.propTypes = {
  purchaseStatus: PropTypes.string,
  enrollment: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  retryTo: PropTypes.string,
}

export default PrivateLessonPurchaseReturn
